/**
 * S445.4 — chunk-load failure recovery.
 *
 * Hash-pinned chunk filenames mean every deploy renames the lazy-loaded
 * modules (`pulse-create-label-KFD342TG.js`). When a user has a tab open
 * and a deploy lands, lazy imports for routes they HAVEN'T already
 * visited 404 on the stale URL — the SW's network-first / cache-fallback
 * protects already-warmed chunks but not cold ones.
 *
 * Symptom pre-fix: clicking "Create label" mid-session goes nowhere
 * with an unhelpful console error.
 *
 * Fix: detect chunk-load failures globally and surface a "New version
 * available — reloading to continue" banner. After a brief delay we
 * hard-reload to pick up the new chunk manifest. The user sees the
 * recovery happen instead of a silent dead app.
 *
 * **Carved out of main.ts** to be unit-testable. The bootstrap entry
 * point calls `installChunkLoadErrorHandler()` once at startup, BEFORE
 * any dynamic import — so even the first lazy load is covered.
 */

const CHUNK_LOAD_ERROR_PATTERNS = [
  // Chrome / Edge / Safari (TypeError messages on dynamic-import 404 or MIME mismatch)
  /Failed to fetch dynamically imported module/i,
  /error loading dynamically imported module/i,
  /Importing a module script failed/i,
  // Firefox
  /Unable to preload CSS for/i,
];

/** Auto-reload delay so the user sees the banner before the page refreshes. */
const RELOAD_DELAY_MS = 4_000;

const BANNER_ID = 'pulse-update-banner';

export function looksLikeChunkLoadError(reason: unknown): boolean {
  const msg =
    reason instanceof Error
      ? reason.message
      : typeof reason === 'string'
        ? reason
        : '';
  if (!msg) return false;
  return CHUNK_LOAD_ERROR_PATTERNS.some((re) => re.test(msg));
}

export interface ShowUpdateBannerOptions {
  /** Test hook — skip the auto-reload timer when set. */
  skipAutoReload?: boolean;
}

export function showUpdateBanner(opts: ShowUpdateBannerOptions = {}): void {
  if (typeof document === 'undefined') return;
  if (document.getElementById(BANNER_ID)) return;
  const banner = document.createElement('div');
  banner.id = BANNER_ID;
  banner.setAttribute('role', 'alert');
  banner.setAttribute('aria-live', 'assertive');
  banner.style.cssText =
    'position:fixed;top:16px;left:50%;transform:translateX(-50%);z-index:10001;' +
    'background:linear-gradient(135deg,#6366f1,#8b5cf6);color:#fff;padding:12px 18px;' +
    'border-radius:8px;font-family:system-ui,-apple-system,sans-serif;font-size:14px;' +
    'font-weight:500;box-shadow:0 8px 24px rgba(0,0,0,.4);' +
    'max-width:min(560px,calc(100vw - 32px));display:flex;align-items:center;gap:12px';
  const text = document.createElement('span');
  text.textContent = 'A new version is available — reloading to continue…';
  const reload = document.createElement('button');
  reload.type = 'button';
  reload.textContent = 'Reload now';
  reload.setAttribute('data-pulse-update-banner-reload', '');
  reload.style.cssText =
    'background:rgba(255,255,255,.18);color:#fff;border:0;padding:6px 12px;' +
    'border-radius:6px;font:inherit;font-size:13px;cursor:pointer;font-weight:600';
  reload.addEventListener('click', () => safelyReload());
  banner.appendChild(text);
  banner.appendChild(reload);
  document.body.appendChild(banner);
  if (!opts.skipAutoReload) {
    window.setTimeout(() => safelyReload(), RELOAD_DELAY_MS);
  }
}

function safelyReload(): void {
  try {
    location.reload();
  } catch {
    // Some test environments (jsdom) refuse navigation; banner stays
    // for the user to click manually. Not a failure mode in browsers.
  }
}

let installed = false;

/**
 * Wire window-level error listeners that catch chunk-load failures and
 * surface the update banner. Idempotent: re-calling is a no-op.
 */
export function installChunkLoadErrorHandler(opts: ShowUpdateBannerOptions = {}): void {
  if (installed) return;
  installed = true;

  window.addEventListener('unhandledrejection', (e) => {
    if (looksLikeChunkLoadError(e.reason)) {
      showUpdateBanner(opts);
    }
  });
  // Generic error event — covers script tag load failures + module worker
  // failures. `ErrorEvent.message` is a string, `ErrorEvent.error` may be null.
  window.addEventListener('error', (e) => {
    if (looksLikeChunkLoadError(e.error) || looksLikeChunkLoadError(e.message)) {
      showUpdateBanner(opts);
    }
  });
}

/** Test-only helper to reset installation state between tests. */
export function _resetChunkLoadHandlerForTests(): void {
  installed = false;
  if (typeof document !== 'undefined') {
    document.getElementById(BANNER_ID)?.remove();
  }
}
